import React from "react";
import Footer from "../FooterBn";

/** Reuse the same UI language as the Guidelines page (chips + cards + soft background). */

const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
    {children}
  </span>
);

const Card = ({ children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
    {children}
  </div>
);

const Section = ({ id, title, subtitle, children }) => (
  <section id={id} className="scroll-mt-24">
    <div className="mb-3">
      <h2 className="text-lg font-black text-slate-900 sm:text-xl">{title}</h2>
      {subtitle ? (
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          {subtitle}
        </p>
      ) : null}
    </div>
    <Card>{children}</Card>
  </section>
);

const AboutGoatTeachers = () => {
  return (
    <>
      <div className="relative isolate bg-slate-50">
        {/* Subtle background blobs (same vibe as guidelines page) */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-[-120px] h-[320px] w-[320px] -translate-x-1/2 rounded-full bg-slate-200/50 blur-3xl" />
          <div className="absolute right-[-120px] top-[180px] h-[320px] w-[320px] rounded-full bg-slate-200/40 blur-3xl" />
          <div className="absolute left-[-120px] top-[420px] h-[320px] w-[320px] rounded-full bg-slate-200/40 blur-3xl" />
        </div>

        <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
          {/* Header */}
          <div className="mb-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="mb-2 flex flex-wrap gap-2">
                  <Chip>About</Chip>
                  <Chip>Student-first</Chip>
                  <Chip>Community-driven</Chip>
                </div>

                <h1 className="text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
                  About Goat Teachers
                </h1>

                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
                  Goat Teachers is a multi-network student platform built around
                  trust, transparency, and practical support—helping students
                  and guardians make better academic decisions and connect with
                  the right help, faster.
                </p>
              </div>

              {/* Quick Nav */}
              <div className="w-full lg:max-w-xs">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    Quick Navigation
                  </div>
                  <div className="mt-3 grid gap-2 text-sm">
                    {[
                      ["vision-mission", "Vision & Mission"],
                      ["what-is", "What is Goat Teachers?"],
                      ["networks", "Our Networks"],
                    ].map(([id, label]) => (
                      <a
                        key={id}
                        href={`#${id}`}
                        className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-700 shadow-sm hover:border-slate-300"
                      >
                        <span className="font-semibold">{label}</span>
                        <span className="text-slate-400 group-hover:text-slate-700">
                          →
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-6">
            {/* Vision + Mission */}
            <Section
              id="vision-mission"
              title="Our Vision and Mission"
              subtitle="Why we exist and what we’re building for students."
            >
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <div className="text-xs font-extrabold uppercase tracking-wide text-slate-600">
                    Vision
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-700">
                    To make student life easier by giving learners the tools,
                    guidance, and resources they need to excel academically—and
                    feel supported throughout their educational journey. Goat
                    Teachers is building a student-first ecosystem where
                    learning, growth, and collaboration are more accessible,
                    more effective, and community-driven.
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <div className="text-xs font-extrabold uppercase tracking-wide text-slate-600">
                    Mission
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-700">
                    Goat Teachers is an Education Technology platform designed
                    to simplify student life through a complete, free, and
                    easy-to-use network of solutions for school, college, and
                    university students. We connect learners with the right
                    teachers, tutoring opportunities, and academic resources—so
                    students can make better decisions, learn faster, and move
                    forward with confidence.
                  </p>
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-sm font-extrabold text-slate-900">
                  What we aim to reduce
                </div>
                <p className="mt-1 text-sm leading-relaxed text-slate-700">
                  We exist to reduce confusion, save time, and make the student
                  journey smoother— powered by community contribution and
                  responsible moderation.
                </p>
              </div>
            </Section>

            {/* What is Goat Teachers */}
            <Section
              id="what-is"
              title="What is Goat Teachers?"
              subtitle="A multi-network platform built around trust, transparency, and practical support."
            >
              <div className="space-y-4">
                <p className="text-sm leading-relaxed text-slate-700">
                  Goat Teachers supports students at every stage of their
                  academic journey—from choosing the right teacher or
                  institution, to finding the right help, to accessing study
                  resources shared by the community.
                </p>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-sm font-extrabold text-slate-900">
                      For Students
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-slate-700">
                      Make confident decisions using real reviews, find tutors,
                      and access learning resources.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-sm font-extrabold text-slate-900">
                      For Guardians
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-slate-700">
                      Discover trusted tuition options and match with tutors
                      based on real needs.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-sm font-extrabold text-slate-900">
                      For Tutors
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-slate-700">
                      Build credibility, connect with students, and grow with a
                      structured tutoring marketplace.
                    </p>
                  </div>
                </div>
              </div>
            </Section>

            {/* Networks */}
            <Section
              id="networks"
              title="Our Networks"
              subtitle="Three connected systems that work together to support students."
            >
              <div className="grid gap-4 lg:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-extrabold text-slate-900">
                        Goat Teachers
                      </div>
                      <p className="mt-1 text-sm leading-relaxed text-slate-700">
                        Student-driven reviews and ratings of institutions and
                        faculty members to help others choose wisely.
                      </p>
                    </div>
                    <span className="rounded-xl border border-slate-200 bg-white px-2 py-1 text-xs font-bold text-slate-700">
                      Reviews
                    </span>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-extrabold text-slate-900">
                        Tutor Network
                      </div>
                      <p className="mt-1 text-sm leading-relaxed text-slate-700">
                        A marketplace connecting students and guardians with
                        tutors for personalized tuition and coaching.
                      </p>
                    </div>
                    <span className="rounded-xl border border-slate-200 bg-white px-2 py-1 text-xs font-bold text-slate-700">
                      Tuition
                    </span>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-extrabold text-slate-900">
                        Resource Network
                      </div>
                      <p className="mt-1 text-sm leading-relaxed text-slate-700">
                        Community sharing of academic materials like notes,
                        assignments, and study resources—focused on learning
                        support.
                      </p>
                    </div>
                    <span className="rounded-xl border border-slate-200 bg-white px-2 py-1 text-xs font-bold text-slate-700">
                      Resources
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-sm font-extrabold text-slate-900">
                  How it all connects
                </div>
                <p className="mt-1 text-sm leading-relaxed text-slate-700">
                  These interconnected networks form the backbone of Goat
                  Teachers—supporting students from discovery and
                  decision-making to tutoring and everyday study support.
                </p>
              </div>
            </Section>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default AboutGoatTeachers;
